/**
 * Projects: list (+ recent ideas) and the project board — workstreams by state with their
 * current task, run state, progress and latest build — plus Landing, Artifacts and Settings tabs.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useChildMatches, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { AgentGlyph, Button, Chip, Sheet } from '@viko/transcript-ui'
import {
  CircleCheck,
  CircleDashed,
  CircleDot,
  GitBranch,
  GitMerge,
  Lightbulb,
  Package,
  Play,
  Plus,
  Search,
  Sparkles,
  TextSearch,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { AddProjectDialog } from '../components/AddProjectDialog.js'
import { Split } from '../components/AppShell.js'
import { ArtifactGallery } from '../components/Artifacts.js'
import { useAppUi } from '../components/app-ui.js'
import { BarkPanel } from '../components/bark/index.js' // bark-triage
import { signBrainJob } from '../components/Capture.js'
import { ProjectChangelog, SinceBadge, SinceLastVisit } from '../components/Changelog.js' // g13-digests
import { FeedbackQueue, FeedbackSettings } from '../components/Feedback.js' // g9-feedback
import { ImportReview, IngestSources } from '../components/Ingest.js' // ingest-sync
import { LandGrantCard } from '../components/LandGrant.js' // land-grant
import { NewProjectDialog } from '../components/NewProjectDialog.js'
import { ProjectCheckouts } from '../components/ProjectCheckouts.js'
import { ProjectConfigSheet } from '../components/ProjectConfigSheet.js'
import { ProjectInventory } from '../components/ProjectInventory.js' // g10-inventory
import { AutonomyEditor, HistoryConsent, LoopPicker } from '../components/ProjectSettings.js'
import { ResearchLibrary } from '../components/research/ResearchLibrary.js' // g8-voice
import { Ago, Empty, errorText, Meter, PageHead, SkeletonRows, useToast } from '../components/ui.js'
import { useWide } from '../components/useWide.js'
import { useBackend } from '../lib/backend.js'
import { isEnabled } from '../lib/flags.js'
import { runHref } from '../lib/ia.js' // ia-shell
import { type LandingChip, landingChips } from '../lib/landing-status.js'
import { defaultProject, rememberProject } from '../lib/last-project.js'
import { qk, useIdeas, useLandings, useProjects } from '../lib/queries.js'
import type { HistoryRun, Project, RunSummary, Workstream } from '../lib/types.js'
import { progressOf } from '../lib/work.js'

export function ProjectsPage() {
  const child = useChildMatches()
  const params = useParams({ strict: false }) as { projectId?: string }
  const projects = useProjects(),
    nav = useNavigate(),
    wide = useWide()
  // Wide layouts open the last-viewed (else first) project instead of an empty detail pane.
  const fallbackId =
    wide && !params.projectId ? defaultProject((projects.data ?? []).map((p) => p.id)) : undefined
  useEffect(() => {
    if (fallbackId)
      void nav({ to: '/projects/$projectId', params: { projectId: fallbackId }, replace: true })
  }, [fallbackId, nav])
  return (
    <Split
      hasDetail={child.length > 0}
      list={<ProjectList current={params.projectId} />}
      detail={params.projectId ? <ProjectBoard projectId={params.projectId} /> : null}
      fallback={
        <Empty center title="Pick a project">
          Boards show each workstream, what it is doing and its latest build.
        </Empty>
      }
    />
  )
}

function ProjectList({ current }: { current?: string }) {
  const projects = useProjects()
  const b = useBackend(),
    qc = useQueryClient(),
    nav = useNavigate()
  const [adding, setAdding] = useState(false)
  const [creating, setCreating] = useState(false)
  return (
    <>
      <PageHead title="Projects">
        {/* account-projects: New project is primary; machine-discovered repos are secondary. */}
        <Button
          size="sm"
          variant="primary"
          icon={<Plus size={14} />}
          onClick={() => setCreating(true)}
        >
          New project
        </Button>
        <Button size="sm" onClick={() => setAdding(true)}>
          Add from a machine
        </Button>
        <span className="flex gap-1 desktop:hidden">
          <Link to="/releases" className="vk-btn vk-btn--ghost vk-btn--sm">
            <span>Releases</span>
          </Link>
          <Link to="/rules" className="vk-btn vk-btn--ghost vk-btn--sm">
            <span>Rules</span>
          </Link>
        </span>
      </PageHead>
      {projects.isPending ? <SkeletonRows n={4} /> : null}
      {projects.isError ? (
        <Empty title="Could not load projects">{errorText(projects.error)}</Empty>
      ) : null}
      {projects.isSuccess && projects.data.length === 0 ? (
        <Empty title="No projects yet">
          Choose New project to start one (Viko can create the GitHub repo), or Add from a machine
          to pick a repository you already have.
        </Empty>
      ) : null}
      {(projects.data ?? []).map((p) => {
        const live = p.workstreams.filter((w) => w.state === 'live' || w.state === 'blocked').length
        const blocked = p.workstreams.filter((w) => w.state === 'blocked').length
        const unknown = p.workstreams.filter((w) => w.state === 'unknown').length
        return (
          <Link
            key={p.id}
            to="/projects/$projectId"
            params={{ projectId: p.id }}
            className="row"
            aria-current={p.id === current ? 'page' : undefined}
          >
            <span
              className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-surface-2 text-[14px] font-bold text-text-2"
              aria-hidden="true"
            >
              {p.name.slice(0, 1)}
            </span>
            <span className="grid min-w-0 flex-1 gap-0.5">
              <span className="row__title">{p.name}</span>
              <span className="row__meta">
                {p.repo} · {p.workstreams.length} workstreams{live ? ` · ${live} live` : ''}
                {blocked ? <span className="text-status-wait"> · {blocked} need you</span> : null}
                {unknown ? <span> · {unknown} unverified</span> : null}
              </span>
            </span>
            {/* ---- g13-digests: changes since the user's last visit ---- */}
            <SinceBadge projectId={p.id} />
            {/* ---- end g13-digests ---- */}
            <span className="vk-chip flex-none">
              <span className="vk-chip__text">{p.loop} loop</span>
            </span>
          </Link>
        )
      })}
      <Ideas />
      {creating ? <NewProjectDialog onClose={() => setCreating(false)} /> : null}
      {adding ? (
        <AddProjectDialog
          key={b.artifactAccess.accountId}
          onClose={() => setAdding(false)}
          onSaved={async (project) => {
            await qc.invalidateQueries({ queryKey: qk.projects })
            void nav({ to: '/projects/$projectId', params: { projectId: project.id } })
          }}
        />
      ) : null}
    </>
  )
}

/** Recent captures and where they went (triage → review card → workstreams/tasks). */
export function Ideas() {
  const ideas = useIdeas()
  const b = useBackend()
  const toast = useToast()
  const list = (ideas.data ?? []).filter((i) => i.status !== 'archived').slice(0, 8)
  if (!list.length) return null
  const sort = async (id: string) => {
    try {
      const job = await b.api.triageIdea(id)
      const ok = await signBrainJob(b, job)
      toast(ok ? 'Viko is sorting it' : 'Queued until a brain machine is online')
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  return (
    <section aria-labelledby="ideas-h" className="pt-4">
      <h2 id="ideas-h" className="section-title">
        Ideas
      </h2>
      <ul className="m-0 list-none p-0">
        {list.map((i) => (
          <li key={i.id} className="row !cursor-default items-center">
            <Lightbulb size={15} className="flex-none text-text-3" aria-hidden="true" />
            <span className="grid min-w-0 flex-1 gap-0.5">
              <span className="truncate text-[14px]">{i.text || '(attachment)'}</span>
              <span className="row__meta">
                {i.status ?? 'new'} · <Ago ts={i.createdAt} />
              </span>
            </span>
            {i.status === 'new' && !b.content ? (
              <Button
                size="sm"
                variant="ghost"
                icon={<Sparkles size={14} />}
                onClick={() => void sort(i.id)}
              >
                Sort
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}

type Tab =
  | 'board'
  | 'changes'
  | 'history'
  | 'landing'
  | 'artifacts'
  | 'settings'
  | 'feedback'
  | 'research' // g9-feedback, g13-digests, g8-voice
  | 'import' // ingest-sync
const TABS: [Tab, string][] = [
  ['board', 'Board'],
  ['changes', 'Changes'],
  ['history', 'History'],
  ['landing', 'Landing'],
  ['artifacts', 'Artifacts'],
  ['feedback', 'Feedback'], // g9-feedback
  ['research', 'Research'], // g8-voice
  ['import', 'Import'], // ingest-sync
  ['settings', 'Settings'],
]

function ProjectBoard({ projectId }: { projectId: string }) {
  const projects = useProjects()
  const search = useSearch({ strict: false }) as { tab?: Tab }
  const nav = useNavigate()
  const tab: Tab = search.tab ?? 'board'
  const p = projects.data?.find((x) => x.id === projectId)
  const [adding, setAdding] = useState(false)
  const ui = useAppUi()
  useEffect(() => {
    if (p) rememberProject(p.id)
  }, [p])
  if (!p) return projects.isPending ? <SkeletonRows /> : <Empty title="Project not found" />
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <PageHead title={p.name}>
        <Button size="sm" icon={<Plus size={14} />} onClick={() => setAdding(true)}>
          Workstream
        </Button>
        <Button
          size="sm"
          variant="primary"
          icon={<Play size={14} />}
          onClick={() => ui.openStart({ projectId: p.id })}
        >
          New session
        </Button>
      </PageHead>
      <p className="m-0 px-4 pb-1 text-[14px] text-text-3">
        {p.goal ? `${p.goal} · ` : ''}
        {p.remote ?? p.repo} · {p.loop} loop
        {p.counts?.proposed ? ` · ${p.counts.proposed} proposed` : ''}
      </p>
      <ProjectCheckouts project={p} />
      {/* ---- bark-triage: bark box + outcome feed until the Lanes tab mounts them ---- */}
      {tab === 'board' && !isEnabled('lanesTab') ? (
        <BarkPanel projectId={p.id} projectName={p.name} />
      ) : null}
      {/* ---- end bark-triage ---- */}
      {/* ---- g13-digests: what changed since this user's last visit (synced marker) ---- */}
      {tab === 'board' && !isEnabled('lanesTab') ? <SinceLastVisit projectId={p.id} /> : null}
      {/* ---- end g13-digests ---- */}
      <div className="tabs px-4" role="tablist" aria-label="Project views">
        {TABS.map(([t, label]) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() =>
              void nav({
                to: '/projects/$projectId',
                params: { projectId },
                search: t === 'board' ? {} : { tab: t },
                replace: true,
              })
            }
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={TABS.find(([t]) => t === tab)?.[1]}>
        {tab === 'board' ? <Board project={p} /> : null}
        {tab === 'history' ? <HistoryTab project={p} /> : null}
        {tab === 'changes' ? <ProjectChangelog projectId={p.id} /> : null}
        {tab === 'landing' ? <LandingTab project={p} /> : null}
        {tab === 'artifacts' ? <ArtifactGallery projectId={p.id} /> : null}
        {tab === 'settings' ? <SettingsTab project={p} /> : null}
        {tab === 'feedback' ? <FeedbackQueue project={p} /> : null /* g9-feedback */}
        {tab === 'research' ? <ResearchLibrary projectId={p.id} /> : null /* g8-voice */}
        {tab === 'import' ? <ImportReview project={p} /> : null /* ingest-sync */}
      </div>
      {adding ? <NewWorkstream project={p} onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

const COLUMNS: { key: Workstream['state'][]; title: string }[] = [
  { key: ['blocked'], title: 'Needs you' },
  { key: ['live'], title: 'Working' },
  { key: ['idle', 'landing'], title: 'Ready' },
  { key: ['unknown'], title: 'Unverified' },
  { key: ['landed'], title: 'Landed' },
]

function Board({ project }: { project: Project }) {
  const landings = useLandings(project.id)
  const keyOf = (w: Workstream) => w.runId ?? `ws:${w.id}`
  const chips = landingChips(
    project.workstreams.map((w) => ({
      runId: keyOf(w),
      workstreamId: w.id,
      projectId: project.id,
    })),
    landings.data,
    [project],
    Date.now(),
  )
  if (!project.workstreams.length)
    return (
      <Empty title="No workstreams yet">
        Start a session to put an agent on this project, capture an idea, or add a workstream and
        give it tasks.
      </Empty>
    )
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-4 px-4 pt-3 pb-8">
      {COLUMNS.map((c) => {
        const ws = project.workstreams.filter((w) => c.key.includes(w.state))
        return (
          <section key={c.title} aria-label={c.title} className="grid min-w-0 content-start gap-2">
            <h3 className="m-0 flex items-center gap-2 text-[14px] font-semibold text-text-2">
              {c.title} <span className="text-text-3">{ws.length}</span>
            </h3>
            {ws.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border-subtle p-3 text-[14px] text-text-3">
                None
              </div>
            ) : null}
            {ws.map((w) => (
              <WorkstreamCard
                key={w.id}
                w={w}
                projectId={project.id}
                landingChip={chips.get(keyOf(w))}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}

const RUN_STATE: Record<Workstream['state'], { label: string; tone: string }> = {
  live: { label: 'Working', tone: 'text-status-run' },
  blocked: { label: 'Needs you', tone: 'text-status-wait' },
  idle: { label: 'Idle', tone: 'text-status-idle' },
  unknown: { label: 'Unknown', tone: 'text-text-3' },
  landing: { label: 'Landing', tone: 'text-accent-text' },
  landed: { label: 'Landed', tone: 'text-status-ok' },
}

function WorkstreamCard({
  w,
  projectId,
  landingChip,
}: {
  w: Workstream
  projectId: string
  landingChip?: LandingChip | undefined
}) {
  const ui = useAppUi()
  const current =
    w.tasks.find((t) => t.status === 'running' || t.status === 'blocked') ??
    w.tasks.find((t) => t.status === 'ready')
  const prog = progressOf(w.tasks)
  const st = RUN_STATE[w.state]
  return (
    <article className="card grid min-w-0 gap-2 p-3" aria-label={w.name}>
      <div className="flex items-start gap-2">
        <Link
          to="/w/$workstreamId"
          params={{ workstreamId: w.id }}
          className="min-w-0 flex-1 text-[14px] font-semibold leading-5 text-text-1"
        >
          {w.name}
        </Link>
        <span className={`flex flex-none items-center gap-1 text-[14px] font-semibold ${st.tone}`}>
          <span className={`pulse${w.state === 'live' ? ' is-live' : ''}`} aria-hidden="true" />
          {st.label}
        </span>
      </div>
      {w.branch ? (
        <span className="flex items-center gap-1.5 text-[14px] text-text-3">
          <GitBranch size={12} aria-hidden="true" />
          <code className="truncate font-mono">{w.branch}</code>
        </span>
      ) : null}
      {current ? (
        <p className="m-0 text-[14px] leading-[18px] text-text-2">
          <span className="text-text-3">Now: </span>
          {current.title}
        </p>
      ) : null}
      <ul className="m-0 grid list-none gap-1 p-0" aria-label="Tasks">
        {w.tasks.slice(0, 5).map((t) => (
          <li key={t.id} className="flex min-w-0 items-center gap-1.5 text-[14px] text-text-3">
            {t.status === 'done' ? (
              <CircleCheck size={12} className="text-status-ok" aria-label="Done" />
            ) : t.status === 'running' ? (
              <CircleDot size={12} className="text-status-run" aria-label="Running" />
            ) : t.status === 'blocked' ? (
              <CircleDot size={12} className="text-status-wait" aria-label="Blocked" />
            ) : t.status === 'unknown' ? (
              <CircleDashed size={12} aria-label="Unverified" />
            ) : (
              <CircleDashed size={12} aria-label={t.status === 'proposed' ? 'Proposed' : 'Ready'} />
            )}
            <span className="truncate">{t.title}</span>
          </li>
        ))}
        {w.tasks.length > 5 ? (
          <li className="text-[14px] text-text-3">+{w.tasks.length - 5} more</li>
        ) : null}
      </ul>
      {prog.total ? (
        <div className="grid gap-1">
          <Meter pct={prog.pct} label={`${w.name} progress`} />
          <span className="text-[14px] text-text-3">
            {prog.done}/{prog.total} done
          </span>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-[14px] text-text-3">
        {landingChip ? (
          <span
            className={`vk-chip vk-tone-${landingChip.tone}`}
            title={landingChip.title ?? landingChip.label}
            data-landing-chip
          >
            <GitMerge size={11} aria-hidden="true" />
            <span className="vk-chip__text">{landingChip.label}</span>
          </span>
        ) : null}
        {w.latestBuild ? (
          <span className="vk-chip">
            <Package size={11} aria-hidden="true" />
            <span className="vk-chip__text">
              Build {w.latestBuild.number} · {w.latestBuild.lane}
            </span>
          </span>
        ) : null}
        {w.runId ? (
          <Link
            to="/now/$runId"
            params={{ runId: w.runId }}
            className="text-[14px] text-accent-text"
          >
            Open session
          </Link>
        ) : null}
        {w.state !== 'live' && w.state !== 'landed' ? (
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            icon={<Play size={13} />}
            aria-label={`New session in ${w.name}`}
            onClick={() =>
              ui.openStart({
                projectId,
                workstreamId: w.id,
                workstreamName: w.name,
                ...(current && current.status === 'ready'
                  ? { taskId: current.id, prompt: current.title }
                  : {}),
                ...(w.machineId ? { machineId: w.machineId } : {}),
              })
            }
          >
            New session
          </Button>
        ) : null}
      </div>
    </article>
  )
}

/**
 * Past sessions: Claude/Codex history imported from each machine and terminal sessions Viko
 * adopted (read-only; open one to read it, resume it in Viko, or copy the terminal command),
 * plus search across every transcript of this project.
 */
export function HistoryTab({ project }: { project: Project }) {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const history = useQuery({
    queryKey: ['history', b.artifactAccess.accountId, project.id],
    queryFn: () => b.api.projectHistory(project.id, { limit: 100 }),
    retry: false,
  })
  const [q, setQ] = useState('')
  const [term, setTerm] = useState('')
  const hits = useQuery({
    queryKey: ['search', b.artifactAccess.accountId, term, project.id],
    queryFn: () => b.api.search(term, { projectId: project.id, limit: 50 }),
    enabled: term.length >= 2,
    retry: false,
  })
  const open = (r: HistoryRun) => {
    qc.setQueryData<RunSummary>(['runinfo', r.runId], {
      ...r,
      agent: r.agent ?? 'claude',
      projectName: project.name,
      updatedAt: r.lastActivityAt,
    } as RunSummary)
    void nav({ href: runHref(r.runId, project.id) }) // ia-shell: in the project's chrome
  }
  const byId = new Map((history.data?.runs ?? []).map((r) => [r.runId, r]))
  const runs = history.data?.runs ?? []
  return (
    <div className="grid gap-2 pt-3 pb-8">
      <form
        className="px-4"
        role="search"
        aria-label={`Search ${project.name} transcripts`}
        onSubmit={(e) => {
          e.preventDefault()
          setTerm(q.trim())
        }}
      >
        <div className="statusask">
          <Search size={15} className="flex-none text-text-3" aria-hidden="true" />
          <input
            className="statusask__input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search every session of this project"
            aria-label="Search transcripts"
          />
          <button
            type="submit"
            className="vk-btn vk-btn--ghost vk-btn--sm"
            disabled={q.trim().length < 2}
          >
            Search
          </button>
        </div>
      </form>
      {term.length >= 2 ? (
        <section aria-label="Search results">
          <h3 className="section-title">
            Results <span className="text-text-3">{hits.data?.length ?? 0}</span>
          </h3>
          {hits.isPending ? <SkeletonRows n={2} /> : null}
          {hits.data && !hits.data.length ? (
            <p className="m-0 px-4 text-[14px] text-text-3">No matches.</p>
          ) : null}
          <ul className="m-0 list-none p-0">
            {(hits.data ?? []).map((h) => {
              const runId = h.runId ?? h.run_id
              const hr = byId.get(runId)
              return (
                <li key={`${runId}:${h.seq}`}>
                  <button
                    type="button"
                    className="row"
                    onClick={() =>
                      hr ? open(hr) : void nav({ href: runHref(runId, project.id, h.seq) })
                    }
                  >
                    <TextSearch
                      size={15}
                      className="mt-0.5 flex-none text-text-3"
                      aria-hidden="true"
                    />
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="hit__snippet text-[14px] leading-5">
                        <Snippet text={h.snippet} />
                      </span>
                      <span className="row__meta">
                        {h.title ?? runId}
                        {h.source && h.source !== 'viko' ? ` · ${h.source}` : ''}
                        {h.ts ? (
                          <>
                            {' '}
                            · <Ago ts={h.ts} />
                          </>
                        ) : null}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}
      <section aria-label="Past sessions">
        <h3 className="section-title">
          Past sessions <span className="text-text-3">{runs.length}</span>
        </h3>
        {history.isPending ? <SkeletonRows n={3} /> : null}
        {history.isSuccess && !runs.length ? (
          <p className="m-0 px-4 text-[14px] text-text-3">
            No imported sessions yet. Enable history sharing in Project Settings and on each daemon
            to upload recent conversations.
          </p>
        ) : null}
        <ul className="m-0 list-none p-0">
          {runs.map((r) => (
            <li key={r.runId}>
              <button type="button" className="row" onClick={() => open(r)}>
                <AgentGlyph agent={r.agent ?? 'claude'} size={15} />
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span className="row__title truncate">{r.title ?? r.runId}</span>
                  <span className="row__meta">
                    {r.typingElsewhere ? (
                      <span className="font-semibold text-status-wait">Live in a terminal</span>
                    ) : r.source === 'adopted' ? (
                      'Terminal session'
                    ) : (
                      'Imported'
                    )}
                    {r.gitBranch ? ` · ${r.gitBranch}` : ''} · <Ago ts={r.lastActivityAt} />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

/** FTS snippet with `[match]` markers → <mark>. */
export function Snippet({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]*\])/g)
  return (
    <>
      {parts.map((p, i) =>
        /^\[.*\]$/.test(p) ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: static split of one string
          <mark key={i}>{p.slice(1, -1)}</mark>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: static split of one string
          <span key={i}>{p}</span>
        ),
      )}
    </>
  )
}

export function LandingTab({ project }: { project: Project }) {
  const landings = useLandings(project.id)
  const list = landings.data ?? []
  if (landings.isPending) return <SkeletonRows n={3} />
  if (!list.length) return <Empty title="No landings yet">Landed branches show up here.</Empty>
  return (
    <ul className="m-0 list-none p-0 pt-2" aria-label="Landings">
      {list.map((l) => (
        <li key={l.id} className="row !cursor-default">
          <GitBranch size={15} className="mt-0.5 flex-none text-text-3" aria-hidden="true" />
          <span className="grid min-w-0 flex-1 gap-0.5">
            <code className="truncate font-mono text-[14px]">{l.branch ?? l.id}</code>
            <span className="row__meta">
              {l.state.replace('_', ' ')}
              {l.step ? ` · ${l.step}` : ''}
              {l.commit ? ` · ${l.commit.slice(0, 8)}` : ''} · <Ago ts={l.updatedAt} />
            </span>
            {l.error ? <span className="text-[14px] text-status-fail">{l.error}</span> : null}
          </span>
          <Chip
            tone={
              l.state === 'landed'
                ? 'ok'
                : l.state === 'failed'
                  ? 'fail'
                  : l.state === 'awaiting_approval'
                    ? 'wait'
                    : undefined
            }
          >
            {l.state === 'awaiting_approval' ? 'needs you' : l.state}
          </Chip>
        </li>
      ))}
    </ul>
  )
}

export function SettingsTab({ project }: { project: Project }) {
  const b = useBackend(),
    qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  return (
    <div className="grid max-w-[640px] gap-6 px-4 pt-3 pb-8">
      {!project.config ? (
        <p className="m-0 rounded-xl bg-status-wait-soft p-3 text-[14px]">
          The hub has no copy of this project’s <code>viko.project.yaml</code> yet, so its policy
          can’t be edited here.
        </p>
      ) : null}
      <Button variant="ghost" onClick={() => setEditing(true)}>
        Edit configuration
      </Button>
      {editing ? (
        <ProjectConfigSheet
          key={b.artifactAccess.accountId}
          project={project}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            await qc.invalidateQueries({ queryKey: qk.projects })
          }}
        />
      ) : null}
      <HistoryConsent project={project} />
      <section className="grid gap-2" aria-labelledby="ps-loop">
        <h3 id="ps-loop" className="m-0 text-[14px] font-semibold">
          Loop profile
        </h3>
        <LoopPicker project={project} />
      </section>
      <section className="grid gap-2" aria-labelledby="ps-auto">
        <h3 id="ps-auto" className="m-0 text-[14px] font-semibold">
          Autonomy
        </h3>
        <AutonomyEditor project={project} />
      </section>
      {/* ---- g10-inventory: stack inventory, required tools, brokered secret uses ---- */}
      <ProjectInventory project={project} />
      {/* ---- end g10-inventory ---- */}
      {/* ---- g9-feedback ---- */}
      <FeedbackSettings project={project} />
      {/* ---- end g9-feedback ---- */}
      {/* ---- ingest-sync: G16 Settings › Sources ---- */}
      <IngestSources project={project} />
      {/* ---- end ingest-sync ---- */}
      {/* ---- land-grant: standing landing grant (create/renew with a passkey, revoke) ---- */}
      <LandGrantCard project={project} />
      {/* ---- end land-grant ---- */}
    </div>
  )
}

export function NewWorkstream({ project, onClose }: { project: Project; onClose: () => void }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const nav = useNavigate()
  const [name, setName] = useState('')
  const [brief, setBrief] = useState('')
  const [busy, setBusy] = useState(false)
  const create = async () => {
    setBusy(true)
    try {
      const w = await b.api.createWorkstream({
        projectId: project.id,
        name: name.trim(),
        ...(brief.trim() ? { brief: brief.trim() } : {}),
      })
      await qc.invalidateQueries({ queryKey: qk.projects })
      onClose()
      void nav({ to: '/w/$workstreamId', params: { workstreamId: w.id } })
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title="New lane"
      subtitle={project.name}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void create()}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        <label className="field">
          <span>Name</span>
          <input
            className="input"
            value={name}
            // biome-ignore lint/a11y/noAutofocus: primary input of the sheet
            autoFocus
            onChange={(e) => setName(e.target.value)}
            placeholder="Jump physics fix"
          />
        </label>
        <label className="field">
          <span>Brief (optional)</span>
          <textarea
            className="input"
            rows={4}
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder="Goal, constraints and what done looks like"
          />
        </label>
      </div>
    </Sheet>
  )
}
